import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ColDef,
  ColGroupDef,
  ColumnApi,
  GridApi,
  GridOptions,
  HeaderValueGetterFunc,
  ICellRendererParams,
  RowClickedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AgActionButtonsComponent } from './action-buttons.component';
import { ImageFormatterComponent } from './ImageFormatterComponent';

/** Tiempo de espera para el debounce del resize en milisegundos */
const RESIZE_DEBOUNCE_MS = 500;
/** Tiempo de espera entre procesamiento de actualizaciones en milisegundos */
const QUEUE_PROCESS_DELAY_MS = 200;

@Component({
  selector: 'gq-grid',
  templateUrl: './grid.component.html',
  styleUrls: ['./grid.component.scss'],
})
export class GridComponent implements OnInit, DoCheck, OnDestroy {
  private readonly iterableDiffer: IterableDiffer<object>;
  private readonly destroy$ = new Subject<void>();

  public sort: SortItem[] = [];
  public searchValue: string = '';
  private _gridOptions: GridOptions | undefined;

  @Input()
  public get gridOptions(): GridOptions | undefined {
    return this._gridOptions;
  }
  public set gridOptions(value: GridOptions | undefined) {
    this._gridOptions = value;
  }

  @Input() public noRowsText = 'No records found';
  @Input() public loadingText = 'Cargando...';

  private _columnDefs: Array<ColDef | ColGroupDef> = [];
  @Input()
  public get columnDefs(): Array<ColDef | ColGroupDef> {
    return this._columnDefs;
  }
  public set columnDefs(value: Array<ColDef | ColGroupDef>) {
    value.forEach((item) => {
      item.headerValueGetter = this.translateHeaders.bind(
        this,
      ) as unknown as HeaderValueGetterFunc<any>;
    });
    this._columnDefs = value;
    this.setColumnns();
  }

  public translateHeaders(parameters: ICellRendererParams): string {
    return this.translate
      .transform(parameters.colDef?.headerName ?? '')
      .toUpperCase();
  }

  private _data: Array<object> = [];
  @Input()
  public get data(): Array<object> {
    return this._data;
  }
  public set data(value: Array<object>) {
    this._data = value;
    this.setDataRow();
  }

  private _showLoadingOverlay = false;
  @Input()
  public get showLoadingOverlay(): boolean {
    return this._showLoadingOverlay;
  }
  public set showLoadingOverlay(value: boolean) {
    this._showLoadingOverlay = value;
    this.setShowLoadingOverlay();
  }

  private _rowClassRules: ((params: any) => string | string[]) | null = null;
  @Input()
  public get rowClassRules(): ((params: any) => string | string[]) | null {
    return this._rowClassRules;
  }
  public set rowClassRules(value: ((params: any) => string | string[]) | null) {
    this._rowClassRules = value;
  }
  @Input() public autoSizeColumnsToFit = true;

  /** Altura de cada fila en píxeles */
  @Input() public rowHeight = 30;

  /** Altura del encabezado en píxeles */
  @Input() public headerHeight = 35;

  /** Campo que identifica de manera única cada fila (ej: 'id'). Si se define, se usarán transacciones para preservar el scroll */
  @Input() public rowIdField: string | null = null;

  @Output() public readonly onReady = new EventEmitter<ApiGrid>();
  @Output() public readonly onSortChanged = new EventEmitter<SortChange>();
  @Output() public readonly onRowClicked = new EventEmitter<ClickedEvent>();
  @Output() public readonly onRowDoubleClicked = new EventEmitter<ClickedEvent>();

  private api: GridApi | null = null;
  private columnApi: ColumnApi | null = null;
  private previousData: Array<object> = [];
  private isProcessingQueue = false;
  private updateQueue: Array<Array<object>> = [];

  constructor(
    iterableDiffers: IterableDiffers,
    private readonly translate: TranslatePipe,
  ) {
    this.iterableDiffer = iterableDiffers.find([]).create();
  }

  ngOnInit(): void {
    this.initializeGridOptions();
    this.setupResizeListener();
  }

  ngDoCheck(): void {
    const changes = this.iterableDiffer.diff(this.data);
    if (changes) {
      this.setDataRow();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onRowClickedInternal(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | undefined;
    if (target) {
      const actionType = target.getAttribute('data-action-type') ?? (target as any)['action-type'];
      this.onRowClicked.emit({
        action: actionType,
        data: event.data,
      });
      this.api?.redrawRows();
    }
  }

  public onRowDoubleClickedInternal(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | undefined;
    if (target) {
      const actionType = target.getAttribute('data-action-type') ?? (target as any)['action-type'];
      this.onRowDoubleClicked.emit({
        action: actionType,
        data: event.data,
      });
      this.api?.redrawRows();
    }
  }

  public onReadyGrid(params: { api: GridApi; columnApi: ColumnApi }): void {
    this.api = params.api;
    this.columnApi = params.columnApi;
    this.setColumnns();
    this.setDataRow();
    this.setShowLoadingOverlay();

    this.onReady.emit({ api: this.api, columnApi: this.columnApi });
    this.api.sizeColumnsToFit();
  }

  private setColumnns(): void {
    this.api?.setColumnDefs(this._columnDefs);
  }

  private setDataRow(): void {
    if (!this.api) return;

    if (this.rowIdField && this.previousData.length > 0) {
      this.enqueueDataUpdate();
    } else {
      this.previousData = [...this._data];
      this.api.setRowData(this.previousData);
    }

    if (this.autoSizeColumnsToFit) {
      this.api.sizeColumnsToFit();
    }
  }

  private enqueueDataUpdate(): void {
    this.updateQueue.push([...this._data]);
    this.processQueue();
  }

  private processQueue(): void {
    if (this.isProcessingQueue || this.updateQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;
    const dataSnapshot = this.updateQueue.shift()!;

    requestAnimationFrame(() => {
      this.applyDataTransaction(dataSnapshot);
      setTimeout(() => {
        this.isProcessingQueue = false;
        if (this.updateQueue.length > 0) {
          this.processQueue();
        }
      }, QUEUE_PROCESS_DELAY_MS);
    });
  }

  private applyDataTransaction(dataSnapshot: Array<object>): void {
    if (!this.api || !this.rowIdField) return;

    const idField = this.rowIdField;
    const previousMap = new Map<unknown, object>();
    const currentMap = new Map<unknown, object>();

    this.previousData.forEach((item) => {
      const record = item as Record<string, unknown>;
      const id = record[idField];
      if (id !== undefined) {
        previousMap.set(id, item);
      }
    });

    dataSnapshot.forEach((item) => {
      const record = item as Record<string, unknown>;
      const id = record[idField];
      if (id !== undefined) {
        currentMap.set(id, item);
      }
    });

    const add: object[] = [];
    const update: object[] = [];
    const remove: object[] = [];

    dataSnapshot.forEach((item) => {
      const record = item as Record<string, unknown>;
      const id = record[idField];
      if (id === undefined) return;

      const previousItem = previousMap.get(id);
      if (!previousItem) {
        add.push(item);
      } else if (JSON.stringify(previousItem) !== JSON.stringify(item)) {
        update.push(item);
      }
    });

    this.previousData.forEach((item) => {
      const record = item as Record<string, unknown>;
      const id = record[idField];
      if (id !== undefined && !currentMap.has(id)) {
        remove.push(item);
      }
    });

    if (add.length > 0 || update.length > 0 || remove.length > 0) {
      this.api.applyTransaction({ add, update, remove });
    }

    this.previousData = [...dataSnapshot];
  }

  private setShowLoadingOverlay(): void {
    if (!this.api) return;

    if (this._showLoadingOverlay) {
      this.api.showLoadingOverlay();
    } else {
      this.api.hideOverlay();
    }
  }

  public quickSearch(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.api?.setQuickFilter(target.value);
  }

  private initializeGridOptions(): void {
    if (this._gridOptions !== undefined) return;

    this.gridOptions = {
      overlayNoRowsTemplate: this.translate.transform(this.noRowsText),
      overlayLoadingTemplate: `<span class="ag-overlay-loading-center">${this.translate.transform(this.loadingText)}</span>`,
      rowData: this._data,
      rowHeight: 55,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      getRowId: this.rowIdField
        ? (params) => params.data[this.rowIdField!]?.toString()
        : undefined,
      getRowClass: (params) => {
        return this.rowClassRules instanceof Function
          ? this.rowClassRules(params)
          : '';
      },
      columnDefs: this._columnDefs,
      context: {
        componentParent: this,
      },
      components: {
        actionButtons: AgActionButtonsComponent,
        imagen: ImageFormatterComponent,
      },
      frameworkComponents: {
        actionButtons: AgActionButtonsComponent,
        imagen: ImageFormatterComponent,
      },
      onSortChanged: (event: SortChangedEvent) => this.handleSortChanged(event),
      onRowClicked: (event: RowClickedEvent) => this.onRowClickedInternal(event),
      onRowDoubleClicked: (event: RowClickedEvent) => this.onRowDoubleClickedInternal(event),
    } as GridOptions;
  }

  private handleSortChanged(event: SortChangedEvent): void {
    this.sort = event.columnApi
      .getColumnState()
      .filter((column) => column.sort !== undefined && column.sort !== null)
      .map((column) => ({
        colId: column.colId,
        sort: column.sort!,
        sortIndex: column.sortIndex,
      }))
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

    this.onSortChanged.emit({
      e: event,
      api: this.api,
      columnApi: this.columnApi,
      sort: this.sort,
    } as SortChange);
  }

  private setupResizeListener(): void {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(RESIZE_DEBOUNCE_MS),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.api?.sizeColumnsToFit();
      });
  }
}

export interface ClickedEvent {
  action: string;
  data: any;
}

export interface ApiGrid {
  api: GridApi;
  columnApi: ColumnApi;
}

export interface SortChange extends ApiGrid {
  e: SortChangedEvent;
  sort: SortItem[];
}

export interface SortItem {
  colId: string;
  sort: string;
  sortIndex?: number | null;
}
